import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import promoCodeService from "services/promoCode.services";
import { selectAuthToken } from "store/auth/selectors";
import PromoCodesSeekerCard from "./components/PromoCodesSeekerCard";
import styles from "./styles.module.scss";

const PromoCodesSeeker = () => {
  const { t } = useTranslation("global");
  const accessToken = useSelector(selectAuthToken);

  const [promoCodes, setPromoCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!accessToken) return;

    const fetchPromoCodes = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await promoCodeService.getSeekerPromoCodes(accessToken);
        const list = Array.isArray(response?.data) ? response.data : [];
        setPromoCodes(list);
      } catch (err) {
        console.error("Error fetching seeker promocodes:", err);
        setError(t("dashboard.seekerPromoCodes.loadError"));
        setPromoCodes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPromoCodes();
  }, [accessToken, t]);

  return (
    <div className={`${styles.pageContainer} ${isLoading ? styles.loading : ""}`}>
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>
          {t("dashboard.seekerPromoCodes.title")}
        </h2>
        <p className={styles.pageSubtitle}>
          {t("dashboard.seekerPromoCodes.subtitle")}
        </p>
      </div>

      {isLoading && (
        <div className={styles.list}>
          {[1, 2].map((i) => (
            <div key={i} className={styles.cardSkeleton}>
              <div className={styles.skeletonImage} />
              <div className={styles.skeletonBody}>
                <div className={styles.skeletonLine} />
                <div className={styles.skeletonLineShort} />
                <div className={styles.skeletonLineShort} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && error && <p className={styles.emptyState}>{error}</p>}

      {!isLoading && !error && promoCodes.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrapper}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 8.5V18C3 19.1046 3.89543 20 5 20H19C20.1046 20 21 19.1046 21 18V8.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M12 3V15M12 15L8 11M12 15L16 11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className={styles.emptyTitle}>
            {t("dashboard.seekerPromoCodes.empty")}
          </h3>
          <p className={styles.emptySubtitle}>
            {t("dashboard.seekerPromoCodes.emptySubtitle")}
          </p>
        </div>
      )}

      {!isLoading && !error && promoCodes.length > 0 && (
        <div className={styles.list}>
          {promoCodes.map((promo) => (
            <PromoCodesSeekerCard key={promo.id} promo={promo} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PromoCodesSeeker;